import * as React from "react";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";

import Drawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";

import Divider from "@material-ui/core/Divider";

//icons
import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

// jaza-soft
import MuiTable from "../../components/MuiTable";

import { isEmpty, toFixedValue } from "../../utils/helpers";
import { useDeepMemo } from "../../hooks";
import { UOM } from "../../constants";
import { convert } from "../../helpers/unitConversionHelper";
import { colorLightGreen, colorOrange } from "../../utils/colors";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const renderStatus = value => (
  <div
    style={{
      backgroundColor: value === "Selected" ? colorLightGreen.dark : colorOrange.dark,
      color: "white",
      padding: "4px 8px",
      borderRadius: 4,
      textAlign: "center"
    }}>
    {value}
  </div>
);

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    maxWidth: 1000,
    minWidth: 800
  },
  drawerHeader: {
    display: "flex",
    width: props => props.drawerWidth || 500,
    alignItems: "center",
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  }
}));

const ExternalRollDrawer = props => {
  const classes = useStyles();
  const { open, availableRollList = [], extAvailableRollList, ledgerList = [], conversionFactorList = [], onDrawerClose } = props;

  const columns = useDeepMemo(() => {
    let columns = [
      { dataKey: "rollNo", title: "F. Roll No" },
      { dataKey: "supplierRollNo", title: "S. Roll No" },
      { dataKey: "status", title: "Status", filterOptions: { filter: true }, render: renderStatus },
      { dataKey: "extStatus", title: "Ext Status", filterOptions: { filter: true } },
      {
        dataKey: "stock",
        title: "Stock",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${toFixedValue(value, 2)} ${UomCodeFromValue[row.uom]}`)
      },
      {
        dataKey: "extStock",
        title: "Ext. Stock",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${toFixedValue(value, 3)} ${UomCodeFromValue[row.uom]}`)
      },
      { dataKey: "warehouse", title: "Warehouse", filterOptions: { filter: true } },
      { dataKey: "extWarehouse", title: "Ext. Warehouse", filterOptions: { filter: true } },
      { dataKey: "usedAt", title: "Used Info", length: 100 }
    ];
    return columns;
  }, []);

  const rows = useDeepMemo(() => {
    if (isEmpty(availableRollList)) return [];
    const fabricItemMap = ledgerList?.filter(l => !isEmpty(l.fabricItem)).reduce((acc, e) => ({ ...acc, [e.fabricItem.id]: e.fabricItem }), {});

    let rows = availableRollList.map(ri => {
      const extRoll = extAvailableRollList?.find(e => e.WFXStockMovementNo === ri.rollNo);
      const fabricItem = fabricItemMap[ri.fabricItemId];
      const uom = fabricItem?.uomLength;
      const stock = uom === UOM.KILOGRAM.value ? ri.weight : convert({ input: ri.length, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList });
      const extStock = extRoll?.StockUnits;
      return {
        rollNo: ri.rollNo,
        supplierRollNo: ri.supplierRollNo,
        uom,
        stock,
        extStock,
        warehouse: ri.currWarehouse,
        extWarehouse: extRoll?.SiteCode,
        extStatus: extRoll?.Status,
        usedAt: extRoll?.UsedAt,
        status:
          extRoll?.Status === "Available" && Math.abs(stock - extStock) <= 0.1 && ri.currWarehouse === extRoll?.SiteCode
            ? "Selected"
            : extRoll?.Status === "Used"
            ? "Used"
            : Math.abs(stock - extStock) > 0.1
            ? "Stock Mismatch"
            : ri.currWarehouse !== extRoll?.SiteCode
            ? "Site Mismatch"
            : "Not Selected"
      };
    });

    return rows;
  }, [availableRollList, extAvailableRollList, ledgerList, conversionFactorList]);

  return (
    <div>
      {/** Drawer For Supplier Rolls Upload Against Fabric Item */}
      <Drawer anchor="right" classes={{ paper: classes.drawerPaper }} open={open} onClose={onDrawerClose}>
        <div className={classes.drawerHeader}>
          <IconButton onClick={onDrawerClose}>
            <ChevronRightIcon />
          </IconButton>
          <div>
            <Typography variant="h5">External Roll Status</Typography>
          </div>
        </div>
        <Divider />
        <div>
          <MuiTable columns={columns} rows={rows} searchable={true} pageable={true} searchKeys={["rollNo", "supplierRollNo"]} title="Rolls" />
        </div>
      </Drawer>
    </div>
  );
};

export default ExternalRollDrawer;
